import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api } from '../../lib/api.ts';
import type { Me } from '../../store/auth.ts';

// Current user for the account hub. Same source Profile.tsx reads —
// GET /api/users/me → { user } — so the identity card always reflects the
// freshly-persisted profile fields (name, nameDv, designations, section, org).
export function useMe(): { me: Me | undefined; isLoading: boolean } {
  const q = useQuery({
    queryKey: ['users', 'me'],
    queryFn: () => api.get<{ user: Me }>('/users/me').then((r) => r.data.user),
  });
  return { me: q.data, isLoading: q.isLoading };
}

// Fetch the auth-gated signature image as an object URL so <img src> works,
// exactly as Profile.tsx does (the file route requires the bearer token, so a
// plain <img src="/api/.../file"> would 401). Returns null when there is no
// signature or the fetch fails (caller falls back to an initials monogram).
export function useSignatureUrl(hasSignature: boolean | undefined): string | null {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    let revoked: string | null = null;
    let cancelled = false;
    async function load(): Promise<void> {
      if (!hasSignature) {
        setUrl(null);
        return;
      }
      try {
        const r = await api.get<Blob>('/users/me/signature/file', { responseType: 'blob' });
        if (cancelled) return;
        const objUrl = URL.createObjectURL(r.data);
        revoked = objUrl;
        setUrl(objUrl);
      } catch {
        if (!cancelled) setUrl(null);
      }
    }
    void load();
    return () => {
      cancelled = true;
      if (revoked) URL.revokeObjectURL(revoked);
    };
  }, [hasSignature]);

  return url;
}

// "council secretary" → "Council Secretary" (roles arrive lowercased/varied
// from the linked apps). Also handles snake_case/kebab-case tokens.
export function titleCase(s: string): string {
  return s
    .replace(/[_-]+/g, ' ')
    .trim()
    .split(/\s+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}
